import { Button, Result, Spin } from 'antd';
import React, { useEffect, useState, useCallback } from 'react';
import { VscStarEmpty } from 'react-icons/vsc';
import { IoReturnUpBackOutline } from 'react-icons/io5';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { GoLinkExternal } from 'react-icons/go';
import { AiOutlinePrinter } from 'react-icons/ai';
import { Avatar } from '@material-ui/core';
import moment from 'moment';

import { request } from '~/components/Instance';
import { getSubdomain } from '~/helpers/getSubdomain';
import { AccountStorage } from '~/storages/account';
import {
  AvatarWrapper,
  Container,
  Content,
  ContentWrapper,
  Date,
  EmailFrom,
  EmailTo,
  InfoWrapper,
  LeftInfoWrapper,
  Name,
  NameAndEmailFromWrapper,
  RightInfoWrapper,
  Subject,
  SubjectIconsWrapper,
  SubjectWrapper,
} from './styles';

/**
 * O e-mail como o cliente recebeu (cabeçalho estilo caixa de entrada + corpo).
 * Usado pela prévia de E-mails enviados e pela gaveta do documento.
 *
 * Props:
 *   - logId:        id do email_log com a prévia guardada
 *   - data:         { title, from_name, email_from, email, sent_at }
 *   - iframeWidth:  largura do corpo (padrão 752, a da prévia original)
 *   - iframeHeight: altura do corpo (padrão 680)
 *   - iframeStyle:  estilos extras do iframe (ex.: sem borda)
 */
export const EmailPreview = ({ logId, data, iframeWidth = 752, iframeHeight = 680, iframeStyle }) => {
  const [iframeSource, setIframeSource] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const load = useCallback(async () => {
    if (!logId) return;
    try {
      setIsLoading(true);
      setHasError(false);
      const { data: blob } = await request(
        'GET',
        `/emails-logs/${logId}/preview`,
        null,
        false,
        {
          headers: {
            Authorization: `Bearer ${AccountStorage.get().token}`,
            'X-Tenant': getSubdomain(),
          },
          responseType: 'blob',
        }
      );
      setIframeSource(URL.createObjectURL(blob));
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [logId]);

  useEffect(() => {
    setIframeSource(null);
    load();
  }, [load]);

  useEffect(() => () => {
    if (iframeSource) URL.revokeObjectURL(iframeSource);
  }, [iframeSource]);

  const info = data || {};

  if (isLoading) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  if (hasError) {
    return (
      <Result
        status="error"
        title="Não foi possível carregar a prévia."
        extra={<Button type="primary" onClick={load}>Tentar novamente</Button>}
      />
    );
  }

  if (!iframeSource) return null;

  return (
    <Container>
      <AvatarWrapper>
        <Avatar size="large" icon="user" />
      </AvatarWrapper>

      <ContentWrapper>
        <SubjectWrapper>
          <Subject>{info.title || ''}</Subject>
          <SubjectIconsWrapper>
            <AiOutlinePrinter />
            <GoLinkExternal />
          </SubjectIconsWrapper>
        </SubjectWrapper>

        <InfoWrapper>
          <LeftInfoWrapper>
            <NameAndEmailFromWrapper>
              <Name>{info.from_name ? info.from_name.split(' ')[0] : 'Contlabs'}</Name>
              <EmailFrom>{info.email_from ? `<${info.email_from}>` : 'noreply@contlabs.com.br'}</EmailFrom>
            </NameAndEmailFromWrapper>
            <EmailTo>Para: {info.email}</EmailTo>
          </LeftInfoWrapper>
          <RightInfoWrapper>
            <Date>{moment(info.sent_at).format('DD [de] MMM [de] YYYY[,] HH:mm')}</Date>
            <VscStarEmpty />
            <IoReturnUpBackOutline />
            <BsThreeDotsVertical />
          </RightInfoWrapper>
        </InfoWrapper>

        <Content>
          <iframe
            width={iframeWidth}
            height={iframeHeight}
            src={iframeSource}
            title="Prévia do e-mail"
            style={Object.assign({ maxWidth: '100%' }, iframeStyle)}
          />
        </Content>
      </ContentWrapper>
    </Container>
  );
};

export default EmailPreview;
